import { useCallback, useEffect, useState } from 'react';
import axios from '../utils/axios';
import type { SsoRevokeResult, SsoRevokeScope, SsoSessionList } from '../types/index';
import { createRequestError } from '../utils/requestErrors';

// The signed-in user's own saved Metrc sign-ins; the server never sends token values.
export async function getSsoSessions(): Promise<SsoSessionList> {
  try {
    const response = await axios.get('/sso/sessions');
    return response.data;
  } catch (error) {
    throw createRequestError(error);
  }
}

// Clears every saved Metrc sign-in of the signed-in user (RetailId's copy only).
export async function revokeAllSsoSessions(): Promise<SsoRevokeResult> {
  try {
    const response = await axios.delete('/sso/sessions');
    return response.data;
  } catch (error) {
    throw createRequestError(error);
  }
}

// Clears one saved sign-in (organization + market) or one of its facilities; the server only ever touches the signed-in user's own rows.
export async function revokeSsoSession(scope: SsoRevokeScope): Promise<SsoRevokeResult> {
  try {
    const response = await axios.delete('/sso/sessions', { params: scope });
    return response.data;
  } catch (error) {
    throw createRequestError(error);
  }
}

type SsoSessionsState = {
  data: SsoSessionList | null;
  error: unknown;
  isLoading: boolean;
};

export default function useSsoSessions() {
  const [reloadKey, setReloadKey] = useState(0);
  const [state, setState] = useState<SsoSessionsState>({ data: null, error: null, isLoading: true });

  useEffect(() => {
    let cancelled = false;
    getSsoSessions()
      .then((data) => {
        if (!cancelled) setState({ data, error: null, isLoading: false });
      })
      .catch((error) => {
        if (!cancelled) setState({ data: null, error, isLoading: false });
      });

    // A response that arrives after unmount or after a newer reload is ignored.
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const refetch = useCallback(() => setReloadKey((key) => key + 1), []);

  return { ...state, refetch };
}
